import { noop } from "lodash";
import AppContext from "PFApp/app_context";
import { Button } from "PFComponents/button";
import ConfirmModal from "PFComponents/confirm/confirm_modal";
import { LoadingDots } from "PFComponents/loading_dots";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { importAccountConfig } from "PFCore/services/accounts/account";
import { GROWL_ACTIONS } from "PFReducers/growl_reducer";
import { useContext, useState } from "react";

import css from "./config_modal.module.scss";

export type AccountConfig = {
  export_account_info: {
    id: number;
  } & Record<string, string>;
  config: Record<string, any>;
};

type ConfigModalProps = {
  config: AccountConfig;
  onClose: () => void;
  onChange: (id: number) => void;
};

export const ConfigModal = ({ config, onClose, onChange }: ConfigModalProps) => {
  const { dispatch } = useContext(AppContext);

  const [loading, setLoading] = useState(false);

  const { id } = config.export_account_info;
  const str = JSON.stringify(config);
  const blob = new Blob([str], { type: "text/json;charset=utf-8;" });
  const { getFormattedErrors } = useResponseErrors();

  const handleSubmitImport = (data) => {
    setLoading(true);
    return importAccountConfig(id, data)
      .then(() => {
        setLoading(false);
        dispatch({
          type: GROWL_ACTIONS.GROWL_RENDER,
          payload: {
            message: "Config imported"
          }
        });
        onChange(id);
      })
      .catch(handleError);
  };

  const handleInputChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      return; // when cancel button pressed in filedialog
    }
    e.target.value = null; // reset so you can trigger change multiple times
    file.text().then((text) => {
      try {
        const jsonConfig = JSON.parse(text);
        handleSubmitImport(jsonConfig);
      } catch {
        dispatch({
          type: GROWL_ACTIONS.GROWL_RENDER,
          payload: {
            message: "invalid file type (expected JSON)",
            kind: "error"
          }
        });
      }
    });
  };

  const handleError = ({ response }) => {
    setLoading(false);
    const allErrors = getFormattedErrors(response);

    const message = (
      <div>
        {Object.keys(allErrors).map((key) => (
          <div key={key}>
            {key}: {allErrors[key]}
          </div>
        ))}
      </div>
    );
    dispatch({
      type: GROWL_ACTIONS.GROWL_RENDER,
      payload: {
        message,
        kind: "error"
      }
    });
  };

  return (
    <ConfirmModal handleClose={onClose} showOKButton={false} style={{ maxWidth: 1000, margin: "0 auto" }}>
      <div className={css.header}>
        <h3 className={css.title}>Config for: {config.config.account.name}</h3>
        <label className={css.label}>
          {navigator.msSaveBlob ? (
            <Button
              kind="primary"
              onClick={
                navigator.msSaveBlob
                  ? (event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      navigator.msSaveBlob?.(blob, `${id}_config.json`);
                    }
                  : noop
              }
            >
              Download
            </Button>
          ) : (
            <Button
              kind="primary"
              tag="a"
              href={`data:text/json;charset=utf-8,${encodeURIComponent(JSON.stringify(config))}`}
              download={`${id}_config.json`}
            >
              Download
            </Button>
          )}
        </label>
        <label className={css.label}>
          <Button kind="primary" tag="a" disabled={loading}>
            {loading ? <LoadingDots /> : "Import"}
          </Button>
          <input
            disabled={loading}
            className={css.input}
            onChange={handleInputChange}
            name="file"
            type="file"
          />
        </label>
      </div>
      <pre className={css.configPreview}>
        <code>{JSON.stringify(config, undefined, 2)}</code>
      </pre>
    </ConfirmModal>
  );
};
